/**
 * OfflineBanner.js — small slim banner pinned under the header when the
 * tab loses network (#189).
 *
 * Driven by $isOnline from services/online-status.js. Stays out of the
 * way when online (renders nothing).
 *
 * Updated 2026-04-17 (#190 revised + user feedback):
 *   - Composer now BLOCKS sends while offline (Thread.js consults the
 *     same atom). Updated copy reflects this — "messages can't be sent
 *     right now" is the truth, not "will send when..."  No silent
 *     queueing means no risk of context-mismatched delivery later.
 *   - Tap-to-retry: clicking the banner re-checks navigator.onLine,
 *     re-runs loadChats, and reconnects realtime. Same recovery the
 *     pull-to-refresh in ChatList does, surfaced as a faster one-tap
 *     affordance for offline agents who don't think to swipe down.
 */

import { html }     from 'htm/preact';
import { useState } from 'preact/hooks';
import { useStore } from '@nanostores/preact';

import { $isOnline }          from '../services/online-status.js';
import { showToast }          from '../stores/ui.js';
import { loadChats }          from '../services/chats-service.js';
import { reconnectRealtime }  from '../services/realtime.js';

export function OfflineBanner() {
  const online = useStore($isOnline);
  const [retrying, setRetrying] = useState(false);
  if (online) return null;

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      // Most reliable connectivity probe: an actual fetch. navigator.onLine
      // can lie (captive-portal Wi-Fi, VPN tunnels) so don't trust it alone.
      const probe = await fetch('/inbox/sw.js?_ping=' + Date.now(), { cache: 'no-store' })
        .then(r => r.ok)
        .catch(() => false);
      if (!probe) {
        showToast('Still offline — try again in a moment', 2500);
        return;
      }
      // Reconnect realtime + reload the chat list so the agent sees fresh
      // state immediately after coming back online.
      reconnectRealtime();
      await loadChats({ filter: 'all' }).catch(() => {});
      showToast('Back online', 1500);
    } finally {
      setRetrying(false);
    }
  };

  return html`
    <button
      class="inbox-offline-banner"
      role="status"
      aria-live="polite"
      onClick=${retry}
      type="button"
      data-qr-id="offline-banner-retry"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <line x1="1" y1="1" x2="23" y2="23"/>
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
        <path d="M10.71 5.05A16 16 0 0 1 22.58 9"/>
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
        <line x1="12" y1="20" x2="12.01" y2="20"/>
      </svg>
      <span>${retrying ? 'Checking connection…' : "You're offline — sending is paused. Tap to retry."}</span>
    </button>
  `;
}
